import React, { useState } from 'react';
import { Eye, Edit, MapPin, Package, Archive, ArchiveRestore, Trash2, MoreVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../../services/adminService';

const TERMINAL_STATUSES = ['livre', 'annule', 'delivered', 'exception'];

const ShipmentTableMobile = ({ shipments, onStatusUpdate, onArchived, onDeleted, userRole }) => {
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [archiveModal, setArchiveModal] = useState(null); // shipment object
  const [restoreModal, setRestoreModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  const getShipmentStatusBadge = (status) => {
    const map = {
      en_attente:       { label: 'En attente',       color: 'bg-yellow-100 text-yellow-800' },
      depose:           { label: 'Déposé',            color: 'bg-blue-100 text-blue-800' },
      en_preparation:   { label: 'En préparation',   color: 'bg-indigo-100 text-indigo-800' },
      stocke:           { label: 'Stocké',            color: 'bg-gray-100 text-gray-700' },
      charge_conteneur: { label: 'Chargé conteneur', color: 'bg-purple-100 text-purple-800' },
      au_port_anvers:   { label: 'Port Anvers',       color: 'bg-cyan-100 text-cyan-800' },
      bateau_parti:     { label: 'Bateau parti',      color: 'bg-sky-100 text-sky-800' },
      bateau_en_mer:    { label: 'En mer',            color: 'bg-sky-100 text-sky-800' },
      en_transit:       { label: 'En transit',        color: 'bg-sky-100 text-sky-800' },
      arrive_abidjan:   { label: 'Arrivé Abidjan',   color: 'bg-teal-100 text-teal-800' },
      douane:           { label: 'Douane',            color: 'bg-orange-100 text-orange-800' },
      en_livraison:     { label: 'En livraison',      color: 'bg-amber-100 text-amber-800' },
      livre:            { label: 'Livré',             color: 'bg-green-100 text-green-800' },
      delivered:        { label: 'Livré',             color: 'bg-green-100 text-green-800' },
      annule:           { label: 'Annulé',            color: 'bg-red-100 text-red-800' },
      exception:        { label: 'Exception',         color: 'bg-red-100 text-red-800' },
    };
    const cfg = map?.[status] || { label: status || '—', color: 'bg-gray-100 text-gray-700' };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg?.color}`}>
        {cfg?.label}
      </span>
    );
  };

  const getPaymentBadge = (paymentStatus) => {
    const map = {
      paye:     { label: 'Payé',     color: 'bg-green-100 text-green-700 border border-green-200' },
      partiel:  { label: 'Partiel',  color: 'bg-orange-100 text-orange-700 border border-orange-200' },
      non_paye: { label: 'Non payé', color: 'bg-red-100 text-red-700 border border-red-200' },
    };
    const cfg = map?.[paymentStatus] || map?.['non_paye'];
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${cfg?.color}`}>
        {cfg?.label}
      </span>
    );
  };

  const getArticlesDisplay = (shipment) => {
    if (shipment?.articlesSummary) return shipment?.articlesSummary;
    if (shipment?.itemsCount > 0) return `${shipment?.itemsCount} article${shipment?.itemsCount > 1 ? 's' : ''}`;
    return '—';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })?.format(amount);
  };

  const getCardClass = (shipment) => {
    if (shipment?.isArchived) return 'bg-gray-100 opacity-70 border-gray-200';
    if (shipment?.status === 'livre' || shipment?.status === 'delivered') return 'bg-gray-50 opacity-80 border-gray-200';
    if (shipment?.status === 'annule' || shipment?.status === 'exception') return 'bg-red-50/30 opacity-80 border-red-100';
    return 'bg-white border-gray-200 hover:border-green-300 hover:shadow-md';
  };

  const closeAllModals = () => {
    setArchiveModal(null);
    setRestoreModal(null);
    setDeleteModal(null);
    setDeleteBlocked(false);
    setActionError('');
  };

  const handleArchive = async () => {
    setActionLoading(true);
    try {
      await adminService?.archiveShipment(archiveModal?.id);
      onArchived?.(archiveModal?.id, true);
      closeAllModals();
    } catch (err) {
      setActionError(err?.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async () => {
    setActionLoading(true);
    try {
      await adminService?.restoreShipment(restoreModal?.id);
      onArchived?.(restoreModal?.id, false);
      closeAllModals();
    } catch (err) {
      setActionError(err?.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = async (shipment) => {
    setOpenMenuId(null);
    setActionError('');
    setDeleteBlocked(false);
    try {
      const deps = await adminService?.checkShipmentDependencies(shipment?.id);
      setDeleteBlocked(deps?.hasHistory);
    } catch (_) {}
    setDeleteModal(shipment);
  };

  const handleDelete = async () => {
    if (deleteBlocked) return;
    setActionLoading(true);
    try {
      await adminService?.deleteShipment(deleteModal?.id);
      onDeleted?.(deleteModal?.id);
      closeAllModals();
    } catch (err) {
      setActionError(err?.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="lg:hidden space-y-3">
      {shipments?.map((shipment) => (
        <div
          key={shipment?.id}
          className={`rounded-xl shadow-sm border overflow-hidden cursor-pointer transition-all ${getCardClass(shipment)}`}
          onClick={() => navigate(`/shipment-detail-management/${shipment?.id}`)}
        >
          <div className="h-0.5 bg-gradient-to-r from-green-600 to-green-400" />
          <div className="p-4 space-y-3">
            {/* Row 1: Reference + Status */}
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Package size={12} className="text-green-700" />
                </div>
                <span className="font-bold text-gray-900 text-sm">{shipment?.reference}</span>
                {shipment?.isArchived && (
                  <span className="text-xs text-gray-400 flex items-center gap-0.5">
                    <Archive size={10} /> Archivé
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {getShipmentStatusBadge(shipment?.status)}
                {getPaymentBadge(shipment?.paymentStatus)}
              </div>
            </div>

            {/* Row 2: Sender + Receiver */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-500 font-medium mb-0.5">Expéditeur</div>
                <div className="text-sm font-medium text-gray-900 leading-tight">{shipment?.senderName || '—'}</div>
                {shipment?.senderCity && (
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <MapPin size={10} className="text-gray-400" />
                    {shipment?.senderCity}
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs text-gray-500 font-medium mb-0.5">Destinataire</div>
                <div className="text-sm font-medium text-gray-900 leading-tight">{shipment?.receiverName || '—'}</div>
                {shipment?.receiverCity && (
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <MapPin size={10} className="text-green-600" />
                    {shipment?.receiverCity}
                  </div>
                )}
              </div>
            </div>

            {/* Row 3: Articles + Amount + Date */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <Package size={12} className="text-gray-400" />
                {getArticlesDisplay(shipment)}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-gray-900">{formatAmount(shipment?.totalAmount)}</span>
                <span className="text-xs text-gray-400">{formatDate(shipment?.createdAt)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-1" onClick={(e) => e?.stopPropagation()}>
              <button
                onClick={(e) => { e?.stopPropagation(); navigate(`/tracking/${shipment?.reference}`); }}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Voir tracking"
              >
                <Eye size={18} />
              </button>
              <button
                onClick={(e) => { e?.stopPropagation(); navigate(`/shipment-detail-management/${shipment?.id}`); }}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Modifier"
              >
                <Edit size={18} />
              </button>
              {/* ⋮ menu */}
              <div className="relative">
                <button
                  onClick={(e) => { e?.stopPropagation(); setOpenMenuId(openMenuId === shipment?.id ? null : shipment?.id); }}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <MoreVertical size={18} />
                </button>
                {openMenuId === shipment?.id && (
                  <div className="absolute right-0 bottom-10 z-50 bg-white border border-gray-200 rounded-xl shadow-lg min-w-[160px] py-1">
                    {!shipment?.isArchived ? (
                      <button
                        onClick={(e) => { e?.stopPropagation(); setOpenMenuId(null); setArchiveModal(shipment); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50"
                      >
                        <Archive size={14} className="text-amber-500" /> Archiver
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e?.stopPropagation(); setOpenMenuId(null); setRestoreModal(shipment); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-green-50"
                      >
                        <ArchiveRestore size={14} className="text-green-500" /> Restaurer
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={(e) => { e?.stopPropagation(); handleDeleteClick(shipment); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} className="text-red-500" /> Supprimer
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {shipments?.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <Package size={32} className="text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Aucun envoi trouvé</p>
        </div>
      )}

      {/* Archive Modal */}
      {archiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4" onClick={() => !actionLoading && closeAllModals()}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full" onClick={(e) => e?.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Archiver l'envoi</h3>
            <p className="text-sm text-gray-600 mb-4">{archiveModal?.reference} — L'envoi sera masqué de la vue Actifs mais conservé en base.</p>
            {actionError && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{actionError}</div>}
            <div className="flex gap-3">
              <button onClick={closeAllModals} disabled={actionLoading} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium disabled:opacity-50">Annuler</button>
              <button onClick={handleArchive} disabled={actionLoading} className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg font-medium disabled:opacity-50">
                {actionLoading ? 'Archivage...' : 'Archiver'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Modal */}
      {restoreModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4" onClick={() => !actionLoading && closeAllModals()}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full" onClick={(e) => e?.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Restaurer l'envoi</h3>
            <p className="text-sm text-gray-600 mb-4">{restoreModal?.reference} — L'envoi réapparaîtra dans la vue Actifs.</p>
            {actionError && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{actionError}</div>}
            <div className="flex gap-3">
              <button onClick={closeAllModals} disabled={actionLoading} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium disabled:opacity-50">Annuler</button>
              <button onClick={handleRestore} disabled={actionLoading} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium disabled:opacity-50">
                {actionLoading ? 'Restauration...' : 'Restaurer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4" onClick={() => !actionLoading && closeAllModals()}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full" onClick={(e) => e?.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Supprimer l'envoi</h3>
            {deleteBlocked ? (
              <>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                  <p className="text-amber-800 text-sm">Cet envoi possède un historique logistique ou financier. Il ne peut pas être supprimé définitivement. Vous pouvez l'archiver.</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={closeAllModals} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium">Fermer</button>
                  <button onClick={() => { closeAllModals(); setArchiveModal(deleteModal); }} className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg font-medium">Archiver</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">Supprimer définitivement <strong>{deleteModal?.reference}</strong> ? Cette action est irréversible.</p>
                {actionError && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{actionError}</div>}
                <div className="flex gap-3">
                  <button onClick={closeAllModals} disabled={actionLoading} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium disabled:opacity-50">Annuler</button>
                  <button onClick={handleDelete} disabled={actionLoading} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium disabled:opacity-50">
                    {actionLoading ? 'Suppression...' : 'Supprimer'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentTableMobile;